import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';

import {Loader} from '@googlemaps/js-api-loader';
import {GoogleMap, Rectangle} from '@react-google-maps/api';
import {useTranslation} from 'react-i18next';

import {ButtonVariant} from 'components/ui-kit/Button';
import {MAP_CENTER} from 'core/constants/env';

import {MapAction, MapContainer} from './styled';

type Bounds = google.maps.LatLngBoundsLiteral;

interface Props {
    value?: string;
    onChange: (value: string) => void;
}

const loader = new Loader({
    apiKey: process.env.REACT_APP_GOOGLE_MAPS_KEY ?? '',
    version: 'weekly',
    language: 'en',
});

const parseBounds = (value?: string): Bounds | null => {
    if (!value) return null;

    try {
        const parsed = JSON.parse(value) as Partial<Bounds>;
        if (
            [parsed.north, parsed.east, parsed.south, parsed.west].every(
                (coordinate) => typeof coordinate === 'number',
            )
        ) {
            return parsed as Bounds;
        }
    } catch {
        const coordinates = value.split(',').map(Number);
        if (coordinates.length === 4 && coordinates.every(Number.isFinite)) {
            const [north, east, south, west] = coordinates;
            return {north, east, south, west};
        }
    }

    return null;
};

const serializeBounds = (bounds: google.maps.LatLngBounds): string =>
    JSON.stringify(bounds.toJSON());

export const BranchBoundsMap = ({value, onChange}: Props) => {
    const {t} = useTranslation();
    const [isLoaded, setIsLoaded] = useState(false);
    const mapRef = useRef<google.maps.Map | null>(null);
    const rectangleRef = useRef<google.maps.Rectangle | null>(null);
    const bounds = useMemo(() => parseBounds(value), [value]);

    useEffect(() => {
        let isMounted = true;
        loader
            .importLibrary('maps')
            .then(() => isMounted && setIsLoaded(true))
            .catch(() => undefined);
        return () => {
            isMounted = false;
        };
    }, []);

    const updateBounds = useCallback(() => {
        const nextBounds = rectangleRef.current?.getBounds();
        if (!nextBounds) return;

        const serializedBounds = serializeBounds(nextBounds);
        if (serializedBounds !== value) onChange(serializedBounds);
    }, [onChange, value]);

    const useVisibleArea = () => {
        const visibleBounds = mapRef.current?.getBounds();
        if (visibleBounds) onChange(serializeBounds(visibleBounds));
    };

    if (!isLoaded) return <MapContainer />;

    return (
        <MapContainer>
            <GoogleMap
                mapContainerStyle={{width: '100%', height: '100%'}}
                center={MAP_CENTER}
                zoom={6}
                onLoad={(map) => {
                    mapRef.current = map;
                    if (bounds) map.fitBounds(bounds);
                }}
            >
                {bounds && (
                    <Rectangle
                        bounds={bounds}
                        options={{
                            editable: true,
                            draggable: true,
                            fillColor: '#6563e9',
                            fillOpacity: 0.14,
                            strokeColor: '#6563e9',
                            strokeWeight: 2,
                        }}
                        onLoad={(rectangle) => {
                            rectangleRef.current = rectangle;
                        }}
                        onBoundsChanged={updateBounds}
                    />
                )}
            </GoogleMap>
            <MapAction
                type="button"
                size="sm"
                variant={ButtonVariant.Secondary}
                onClick={useVisibleArea}
            >
                {t('pages.profile.branchesNk.form.useVisibleArea')}
            </MapAction>
        </MapContainer>
    );
};
